import {
  computeOpeningStatus,
  parsePeriodsAttribute,
  parseSpecialAttribute,
  refreshAllOpeningStatuses,
  updateOpeningBadge,
} from "./opening-hours-status";

document.addEventListener("DOMContentLoaded", () => {
  const grid = document.querySelector("[data-restaurants-grid]");

  if (!grid) {
    return;
  }

  const cards = Array.from(grid.querySelectorAll("[data-restaurant-card]"));
  const filters = {
    type: document.querySelector('[data-restaurants-filter="type"]'),
    subtype: document.querySelector('[data-restaurants-filter="subtype"]'),
    rating: document.querySelector('[data-restaurants-filter="rating"]'),
    cuisine: document.querySelector('[data-restaurants-filter="cuisine"]'),
    service: document.querySelector('[data-restaurants-filter="service"]'),
    open: document.querySelector('[data-restaurants-filter="open"]'),
  };
  // Category pages keep their own URL and pre-set the filters that match it.
  // Those values are the page's baseline: they stay out of the query string and
  // "Filter zurücksetzen" returns to them instead of to the unfiltered list.
  const presetState = {
    type: filters.type?.dataset.restaurantsPreset || "",
    subtype: filters.subtype?.dataset.restaurantsPreset || "",
    rating: filters.rating?.dataset.restaurantsPreset || "",
    cuisine: filters.cuisine?.dataset.restaurantsPreset || "",
    service: filters.service?.dataset.restaurantsPreset || "",
    open: filters.open?.dataset.restaurantsPreset === "1",
  };
  const subtypeField = document.querySelector("[data-restaurants-subtype-field]");
  const ratingField = document.querySelector("[data-restaurants-rating-field]");
  const ratingParentValue =
    ratingField?.dataset.restaurantsRatingParent || "unterkunft";
  const subtypePlaceholder = filters.subtype
    ? filters.subtype.querySelector('option[value=""]')?.textContent ||
      "Kategorie auswählen"
    : "";
  const subtypeOptions = filters.subtype
    ? Array.from(filters.subtype.options)
        .filter((option) => option.value)
        .map((option) => ({
          parent: option.dataset.parent || "",
          text: option.textContent || "",
          value: option.value,
        }))
    : [];
  const resetButton = document.querySelector("[data-restaurants-reset]");
  const emptyResetButton = document.querySelector("[data-restaurants-empty-reset]");
  const openToggleLabel = document.querySelector("[data-restaurants-open-label]");
  const countTarget = document.querySelector("[data-restaurants-count]");
  const countLabelTarget = document.querySelector("[data-restaurants-count-label]");
  const emptyState = document.querySelector("[data-restaurants-empty]");
  const reducedMotionQuery = window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;
  const cardCleanups = new WeakMap();
  let gridCleanup = null;
  const filterAnimationDuration = 220;
  const flipMoveDuration = 300;
  const countedFilterKeys = ["type", "subtype", "rating", "cuisine", "service"];
  const openStateRefreshInterval = 60000;
  const urlParamMap = {
    type: "typ",
    subtype: "art",
    rating: "bewertung",
    cuisine: "kueche",
    service: "service",
    open: "offen",
  };

  const cardPeriods = new Map();
  const cardSpecial = new Map();

  cards.forEach((card) => {
    // Prefer data-periods on the card; keep a parsed cache for filter refresh.
    const periods = parsePeriodsAttribute(card.getAttribute("data-periods"));
    const special = parseSpecialAttribute(card.getAttribute("data-special"));
    cardPeriods.set(card, periods);
    cardSpecial.set(card, special);

    // Mark for the shared Berlin-time refresher (footer/singles/cards).
    if ((periods.length || special.length) && !card.hasAttribute("data-opening-status")) {
      card.setAttribute("data-opening-status", "1");
      card.setAttribute("data-opening-compact", "1");
    }
  });

  // Recompute open/closed from Europe/Berlin so page cache + tourist TZs stay correct.
  const refreshOpenStates = () => {
    let changed = false;

    cards.forEach((card) => {
      const periods = cardPeriods.get(card) || [];
      const special = cardSpecial.get(card) || [];

      if (!periods.length && !special.length) {
        return;
      }

      const opening = computeOpeningStatus(periods, { compact: true, special });
      const openValue = opening.open ? "1" : "0";
      const prevOpen = card.dataset.open;

      card.dataset.open = openValue;
      card.setAttribute("data-opening-state", opening.state);

      const badge = card.querySelector(".restaurants-card__opening");
      if (badge) {
        updateOpeningBadge(badge, opening);
      }

      if (prevOpen !== openValue) {
        changed = true;
      }
    });

    // Keep any other [data-opening-status] nodes (footer, singles) in sync too.
    refreshAllOpeningStatuses();

    return changed;
  };

  const includesToken = (value, token) => {
    if (!token) {
      return true;
    }

    return (value || "").split(/\s+/).includes(token);
  };

  const getOptionBaseLabel = (option) => {
    if (option.dataset.filterLabel) {
      return option.dataset.filterLabel;
    }

    const label = (option.textContent || "").replace(/\s+\(\d+\)\s*$/, "");
    option.dataset.filterLabel = label;

    return label;
  };

  const syncSubtypeOptions = () => {
    if (!filters.subtype) {
      return;
    }

    const parentValue = filters.type ? filters.type.value : "";
    const selectedValue = filters.subtype.value;
    const matchingOptions = subtypeOptions.filter(
      (option) => option.parent === parentValue,
    );

    filters.subtype.textContent = "";

    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = subtypePlaceholder;
    filters.subtype.append(placeholder);

    matchingOptions.forEach((option) => {
      const element = document.createElement("option");
      element.value = option.value;
      element.textContent = option.text;
      element.dataset.parent = option.parent;
      filters.subtype.append(element);
    });

    const isSubtypeAvailable = Boolean(parentValue && matchingOptions.length);
    filters.subtype.disabled = !isSubtypeAvailable;

    if (subtypeField) {
      subtypeField.hidden = !isSubtypeAvailable;
    }

    if (
      !isSubtypeAvailable ||
      !matchingOptions.some((option) => option.value === selectedValue)
    ) {
      filters.subtype.value = "";
    } else {
      filters.subtype.value = selectedValue;
    }
  };

  const syncRatingFilter = () => {
    if (!filters.rating) {
      return;
    }

    const isRatingParentSelected = filters.type
      ? filters.type.value === ratingParentValue
      : false;

    filters.rating.disabled = !isRatingParentSelected;

    if (ratingField) {
      ratingField.hidden = !isRatingParentSelected;
    }

    if (!isRatingParentSelected) {
      filters.rating.value = "";
    }
  };

  const getState = () => ({
    type: filters.type ? filters.type.value : "",
    subtype: filters.subtype ? filters.subtype.value : "",
    rating: filters.rating ? filters.rating.value : "",
    cuisine: filters.cuisine ? filters.cuisine.value : "",
    service: filters.service ? filters.service.value : "",
    open: filters.open ? filters.open.checked : false,
  });

  const cardMatches = (card, state) =>
    includesToken(card.dataset.type, state.type) &&
    includesToken(card.dataset.subtype, state.subtype) &&
    (!state.rating || card.dataset.rating === state.rating) &&
    includesToken(card.dataset.cuisine, state.cuisine) &&
    includesToken(card.dataset.service, state.service) &&
    (!state.open || card.dataset.open === "1");

  const getPotentialCount = (filterKey, value, state) => {
    const potentialState = { ...state, [filterKey]: value };

    return cards.reduce(
      (count, card) => count + (cardMatches(card, potentialState) ? 1 : 0),
      0,
    );
  };

  const updateOptionCounts = (state) => {
    countedFilterKeys.forEach((filterKey) => {
      const filter = filters[filterKey];

      if (!filter || filter.tagName !== "SELECT") {
        return;
      }

      Array.from(filter.options).forEach((option) => {
        const baseLabel = getOptionBaseLabel(option);

        if (!option.value) {
          option.textContent = baseLabel;
          return;
        }

        const count = getPotentialCount(filterKey, option.value, state);

        option.textContent = `${baseLabel} (${count})`;
        option.disabled = 0 === count && option.value !== filter.value;
      });
    });

    if (filters.open && openToggleLabel) {
      const openCount = getPotentialCount("open", true, state);

      if (!openToggleLabel.dataset.filterLabel) {
        openToggleLabel.dataset.filterLabel = (
          openToggleLabel.textContent || ""
        ).replace(/\s+\(\d+\)\s*$/, "");
      }

      openToggleLabel.textContent = `${openToggleLabel.dataset.filterLabel} (${openCount})`;
      filters.open.disabled = 0 === openCount && !state.open;
    }
  };

  const shouldAnimateCards = () =>
    !reducedMotionQuery || !reducedMotionQuery.matches;

  const finalizeCardAnimation = (card) => {
    const cleanup = cardCleanups.get(card);

    if (cleanup) {
      cleanup();
    }
  };

  const clearCardAnimationStyles = (card) => {
    card.classList.remove("is-filtering-in", "is-filtering-out");
    card.style.position = "";
    card.style.top = "";
    card.style.left = "";
    card.style.width = "";
    card.style.height = "";
    card.style.margin = "";
    card.style.transition = "";
    card.style.transform = "";
  };

  const finalizeGridAnimation = () => {
    if (gridCleanup) {
      gridCleanup();
    }
  };

  const applyCardVisibility = (changes) => {
    finalizeGridAnimation();

    changes.forEach(({ card, isVisible }) => {
      finalizeCardAnimation(card);
      clearCardAnimationStyles(card);
      card.hidden = !isVisible;
    });
  };

  const animateCardVisibility = (changes) => {
    finalizeGridAnimation();

    const gridRect = grid.getBoundingClientRect();
    const firstHeight = gridRect.height;
    const firstRects = new Map();
    const wasExiting = new Set();
    const entering = [];
    const moving = [];

    // Capture the current on-screen position of every visible card —
    // including cards still mid-animation — before finalizing, so an
    // interrupted animation continues from where it visually is.
    changes.forEach(({ card }) => {
      if (!card.hidden) {
        firstRects.set(card, card.getBoundingClientRect());
      }

      if (card.classList.contains("is-filtering-out")) {
        wasExiting.add(card);
      }

      finalizeCardAnimation(card);
      card.style.transition = "";
      card.style.transform = "";
    });

    changes.forEach(({ card, isVisible }) => {
      const firstRect = firstRects.get(card);

      if (isVisible) {
        if (firstRect) {
          // Covers cards resurrected mid-exit: back into the flow, the FLIP
          // pass below moves them from their measured ghost position.
          card.hidden = false;
        } else {
          card.hidden = false;
          card.classList.add("is-filtering-in");
          entering.push(card);
        }

        return;
      }

      if (!firstRect || wasExiting.has(card)) {
        card.hidden = true;
        card.classList.remove("is-filtering-in", "is-filtering-out");
        return;
      }

      // Take the leaving card out of the flow and pin it to its old spot so
      // it can fade out while the grid reflows around it.
      card.classList.add("is-filtering-out");
      card.style.position = "absolute";
      card.style.top = `${firstRect.top - gridRect.top}px`;
      card.style.left = `${firstRect.left - gridRect.left}px`;
      card.style.width = `${firstRect.width}px`;
      card.style.height = `${firstRect.height}px`;
      card.style.margin = "0";

      const timer = window.setTimeout(
        () => finalizeCardAnimation(card),
        filterAnimationDuration,
      );

      cardCleanups.set(card, () => {
        window.clearTimeout(timer);
        cardCleanups.delete(card);
        card.hidden = true;
        clearCardAnimationStyles(card);
      });
    });

    // Animate the grid's own height so the content below it doesn't jump.
    const lastHeight = grid.offsetHeight;
    const heightChanged = Math.round(firstHeight) !== Math.round(lastHeight);

    if (heightChanged) {
      grid.style.transition = "none";
      grid.style.height = `${firstHeight}px`;
    }

    // FLIP: put remaining cards back on their old position, then release the
    // transform so they glide into their new grid slot.
    changes.forEach(({ card, isVisible }) => {
      const firstRect = firstRects.get(card);

      if (!isVisible || !firstRect) {
        return;
      }

      const lastRect = card.getBoundingClientRect();
      const deltaX = firstRect.left - lastRect.left;
      const deltaY = firstRect.top - lastRect.top;

      if (deltaX || deltaY) {
        card.style.transition = "none";
        card.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
        moving.push(card);
      }
    });

    void grid.offsetWidth;

    moving.forEach((card) => {
      card.style.transition = `transform ${flipMoveDuration}ms cubic-bezier(0.2, 0, 0, 1)`;
      card.style.transform = "";

      const timer = window.setTimeout(
        () => finalizeCardAnimation(card),
        flipMoveDuration + 50,
      );

      cardCleanups.set(card, () => {
        window.clearTimeout(timer);
        cardCleanups.delete(card);
        card.style.transition = "";
        card.style.transform = "";
      });
    });

    if (heightChanged) {
      grid.style.transition = `height ${flipMoveDuration}ms cubic-bezier(0.2, 0, 0, 1)`;
      grid.style.height = `${lastHeight}px`;

      const timer = window.setTimeout(
        () => finalizeGridAnimation(),
        flipMoveDuration + 50,
      );

      gridCleanup = () => {
        window.clearTimeout(timer);
        gridCleanup = null;
        grid.style.transition = "";
        grid.style.height = "";
      };
    }

    entering.forEach((card) => {
      card.classList.remove("is-filtering-in");
    });
  };

  const applyStateFromUrl = () => {
    const params = new URLSearchParams(window.location.search);

    Object.entries(urlParamMap).forEach(([key, param]) => {
      const filter = filters[key];

      if (!filter || !params.has(param)) {
        return;
      }

      const value = params.get(param) || "";

      if (key === "open") {
        filter.checked = "1" === value;
        return;
      }

      if (Array.from(filter.options || []).some((option) => option.value === value)) {
        filter.value = value;
      }
    });
  };

  const syncUrl = (state) => {
    if (!window.history || !window.history.replaceState) {
      return;
    }

    const url = new URL(window.location.href);

    Object.entries(urlParamMap).forEach(([key, param]) => {
      const value = "open" === key ? (state.open ? "1" : "") : state[key];
      const preset =
        "open" === key ? (presetState.open ? "1" : "") : presetState[key];

      if (value && value !== preset) {
        url.searchParams.set(param, value);
      } else {
        url.searchParams.delete(param);
      }
    });

    window.history.replaceState(null, "", url);
  };

  const applyFilters = ({ animate = true } = {}) => {
    refreshOpenStates();
    syncSubtypeOptions();
    syncRatingFilter();

    const state = getState();

    updateOptionCounts(state);
    syncUrl(state);

    const changes = cards.map((card) => ({
      card,
      isVisible: cardMatches(card, state),
    }));
    const visibleCount = changes.filter(({ isVisible }) => isVisible).length;

    if (animate && shouldAnimateCards()) {
      animateCardVisibility(changes);
    } else {
      applyCardVisibility(changes);
    }

    if (countTarget) {
      countTarget.textContent = String(visibleCount);
    }

    // Zahl und Wort stehen zusammen in einer aria-live-Region — wird nur die
    // Zahl getauscht, liest der Screenreader „1 Unterkünfte".
    if (countLabelTarget) {
      const nextLabel =
        visibleCount === 1
          ? countLabelTarget.dataset.countLabelOne
          : countLabelTarget.dataset.countLabelOther;

      if (nextLabel && countLabelTarget.textContent !== nextLabel) {
        countLabelTarget.textContent = nextLabel;
      }
    }

    if (emptyState) {
      emptyState.hidden = visibleCount > 0;
    }

    if (resetButton) {
      resetButton.disabled = !(
        countedFilterKeys.some((key) => state[key] !== presetState[key]) ||
        state.open !== presetState.open
      );
    }
  };

  Object.values(filters).forEach((filter) => {
    if (!filter) {
      return;
    }

    filter.addEventListener("change", applyFilters);
  });

  const resetFilters = () => {
    Object.entries(filters).forEach(([key, filter]) => {
      if (!filter) {
        return;
      }

      if (key === "open") {
        filter.checked = presetState.open;
      } else {
        filter.value = presetState[key] || "";
      }
    });

    applyFilters();
  };

  [resetButton, emptyResetButton].forEach((button) => {
    if (button) {
      button.addEventListener("click", resetFilters);
    }
  });

  applyStateFromUrl();
  applyFilters({ animate: false });

  if (cards.some((card) => (cardPeriods.get(card) || []).length > 0)) {
    window.setInterval(() => {
      if (refreshOpenStates()) {
        applyFilters();
      }
    }, openStateRefreshInterval);

    window.addEventListener("pageshow", (event) => {
      if (event.persisted && refreshOpenStates()) {
        applyFilters({ animate: false });
      }
    });
  }
});
